import React, { useState, useEffect } from 'react';
import { Box, Container, Typography, CircularProgress, Alert } from '@mui/material';
import { marked } from 'marked';
import DOMPurify from 'dompurify';
import Navbar from '../components/landing/Navbar';
import { COLORS } from '../utils/colors';
import { API_URL } from '../utils/api';

interface LegalDoc {
  id: string;
  slug: string;
  title: string;
  version: string;
  body: string | null;
  bodyFormat: string;
  status: string;
  effectiveDate: string | null;
  updatedAt: string | null;
}

interface LegalPageProps {
  slug: string;
}

// Markdown → marked → sanitize; raw HTML → sanitize. DOMPurify strips scripts/handlers.
const renderBody = (doc: LegalDoc): string => {
  const raw = doc.body || '';
  const html = doc.bodyFormat === 'html' ? raw : (marked.parse(raw, { breaks: true }) as string);
  return DOMPurify.sanitize(html);
};

const LegalPage: React.FC<LegalPageProps> = ({ slug }) => {
  const [doc, setDoc] = useState<LegalDoc | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError('');
    setDoc(null);
    (async () => {
      try {
        const res = await fetch(`${API_URL}/legal/${encodeURIComponent(slug)}`);
        const data = await res.json();
        if (cancelled) return;
        if (res.ok && data.success) setDoc(data.document);
        else setError(data.error || 'This document is not available yet.');
      } catch {
        if (!cancelled) setError('Failed to load this document.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [slug]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', bgcolor: COLORS.paper }}>
      <Navbar />

      <Container maxWidth="md" sx={{ flex: 1, py: { xs: 4, md: 6 } }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress sx={{ color: COLORS.primary }} />
          </Box>
        ) : error || !doc ? (
          <Alert severity="info">{error || 'This document is not available yet.'}</Alert>
        ) : (
          <Box component="article">
            <Typography variant="h3" sx={{ fontWeight: 700, color: COLORS.ink, mb: 1, fontSize: { xs: '1.9rem', md: '2.5rem' } }}>
              {doc.title}
            </Typography>

            <Typography variant="caption" sx={{ color: COLORS.inkMuted, display: 'block', mb: 4 }}>
              Version {doc.version}
              {doc.effectiveDate ? ` · Effective ${String(doc.effectiveDate).slice(0, 10)}` : ''}
            </Typography>

            {/* Sanitized body — cleaned by DOMPurify before render. */}
            <Box
              sx={{
                color: COLORS.inkMuted,
                fontSize: '1.05rem',
                '& h1, & h2, & h3, & h4': { color: COLORS.ink, fontWeight: 700, mt: 3.5, mb: 1.5, lineHeight: 1.3 },
                '& h1': { fontSize: '1.8rem' },
                '& h2': { fontSize: '1.5rem' },
                '& h3': { fontSize: '1.25rem' },
                '& p': { lineHeight: 1.8, mb: 2 },
                '& a': { color: COLORS.primary, textDecoration: 'underline' },
                '& img': { maxWidth: '100%', height: 'auto', borderRadius: 2, my: 2 },
                '& ul, & ol': { pl: 3, mb: 2, '& li': { mb: 0.75, lineHeight: 1.7 } },
                '& blockquote': {
                  borderLeft: `4px solid ${COLORS.primary}`, pl: 2, ml: 0, my: 2,
                  color: COLORS.inkMuted, fontStyle: 'italic',
                },
                '& table': { borderCollapse: 'collapse', width: '100%', my: 2 },
                '& th, & td': { border: '1px solid #E6E0D6', px: 1, py: 0.75, textAlign: 'left' },
                '& th': { bgcolor: '#F0EDE6', fontWeight: 700 },
                '& code': { bgcolor: '#F0EDE6', px: 0.75, py: 0.25, borderRadius: 1, fontFamily: 'monospace', fontSize: '0.9em' },
                '& pre': { bgcolor: COLORS.ink, color: '#fff', p: 2, borderRadius: 2, overflow: 'auto', '& code': { bgcolor: 'transparent', color: 'inherit', p: 0 } },
                '& hr': { border: 'none', borderTop: '1px solid #E6E0D6', my: 4 },
              }}
              dangerouslySetInnerHTML={{ __html: renderBody(doc) }}
            />
          </Box>
        )}
      </Container>
    </Box>
  );
};

export default LegalPage;
